<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { CalendarMonth } from "$lib/components/ui/calendar-month"
  import { Checkbox } from "$lib/components/ui/checkbox"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import type { _response } from "$lib/data/type/_response"
  import { createFilter } from "$lib/hooks/filter.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { type CalendarDate, getLocalTimeZone, today } from "@internationalized/date"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import CalendarIcon from "@lucide/svelte/icons/calendar"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import SaveIcon from "@lucide/svelte/icons/save"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import DialogFormApproval from "./_components/dialog-form-approval.svelte"
  import type { Activity, Relation } from "./_types"

  type ActivityFilter = {
    category: Relation
    sales: Relation
    week: Relation
    date: CalendarDate
  }

  const pageSize = 20
  const prefix = $derived(page.params.prefix ?? "0")

  const filter = createFilter<ActivityFilter>({
    category: {
      id: 0,
      name: "",
      order: 0
    },
    sales: {
      id: 0,
      name: "",
      order: 0
    },
    week: {
      id: 0,
      name: "",
      order: 0
    },
    date: today(getLocalTimeZone()).set({ day: 1 })
  })

  let bottomOfPage = $state<HTMLDivElement>()

  let selected = $state<Activity>()
  let selects = $state<Record<string, boolean>>({})
  const detailDialog = useDialog()
  const approvalDialog = useDialog()

  const date = $derived(filter.state.date.toString())

  const categories = createQuery(() => ({
    queryKey: ["approval-real-category", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/approval-reals/master-category`)
      return response.data.result ?? []
    }
  }))

  const salesPersons = createQuery(() => ({
    queryKey: ["approval-real-sales", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/approval-reals/master-sales-person`)
      return response.data.result ?? []
    }
  }))

  const weeks = createQuery(() => ({
    queryKey: ["approval-real-week", {
      prefix,
      date
    }],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/approval-reals/master-week`, {
        params: {
          date
        }
      })
      return response.data.result ?? []
    }
  }))

  const query = createInfiniteQuery(() => ({
    queryKey: ["activity-approval-real", {
      prefix,
      filter: filter.state
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Activity[]>>(`/api/${prefix}/activity/approval-reals`, {
        params: {
          page: pageParam,
          limit: pageSize,
          category_id: filter.state.category.id,
          sales_id: filter.state.sales.id,
          date,
          week: filter.state.week.id
        }
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))

  const rows = $derived(query.data?.pages.flat() ?? [])

  function handleApprove() {
    if (!Object.values(selects).some(Boolean)) {
      toast.info("Checklist kosong")
      return
    }
    approvalDialog.setOpen(true)
  }

  $effect(() => {
    if (!bottomOfPage || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 },
    )

    observer.observe(bottomOfPage)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Approval Realisasi</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group shrink-0"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={() => query.refetch()}>
        {#if query.isFetching}
          <LoaderCircleIcon
            class="animate-spin"
            aria-hidden="true"/>
        {:else}
          <RefreshCwIcon aria-hidden="true"/>
        {/if}
      </Button>
      <div class="min-w-0 flex-1">
        <Popover>
          <PopoverButton
            class="w-full"
            value={filter.state.sales.name || "Semua Sales"}/>
          <PopoverContent>
            <Command>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each salesPersons.data ?? [] as item (item.id)}
                    <CommandItem
                      value={item.name}
                      onclick={() => {
                        filter.set("sales", item)
                        document.keyEsc()
                      }}>
                      {item.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              {...props}
              variant="outline"
              size="icon"
              aria-label="Filter">
              <FilterIcon aria-hidden="true"/>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          class="relative grid w-72 gap-4 p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <div class="space-y-1">
            <Label>Kategori</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={filter.state.category.name || "Semua Kategori"}/>
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each categories.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => {
                            filter.set("category", item)
                            document.keyEsc()
                          }}>
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <div class="min-w-0 flex-1">
        <Popover>
          <PopoverTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                variant="outline"
                class="w-full justify-start text-left font-normal">
                <CalendarIcon aria-hidden="true"/>
                {filter.state.date.format("MMMM yyyy")}
              </Button>
            {/snippet}
          </PopoverTrigger>
          <PopoverContent align="start">
            <CalendarMonth
              selected={filter.state.date}
              onSelect={(value) => {
                filter.set("date", value)
                filter.set("week", { id: 0, name: "", order: 0 })
                document.keyEsc()
              }}/>
          </PopoverContent>
        </Popover>
      </div>
      <div class="min-w-0 flex-1">
        <Popover>
          <PopoverButton
            class="w-full tabular-nums tracking-tight"
            value={filter.state.week.name || "Minggu"}/>
          <PopoverContent>
            <Command>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each weeks.data ?? [] as item (item.id)}
                    <CommandItem
                      value={item.name}
                      onclick={() => {
                        filter.set("week", item)
                        document.keyEsc()
                      }}>
                      {item.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  </nav>
  <div class="min-h-full">
    {#if !query.isFetching && rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {/if}
    {#if query.isError}
      <div class="px-3 py-4 text-center text-xs">Gagal memuat data</div>
    {/if}
    {#each rows as activity (activity.id)}
      <div class="flex items-center gap-4 px-3 py-2 text-sm hover:bg-muted">
        <Button
          variant="ghost"
          class="block h-auto min-w-0 flex-1 justify-start whitespace-normal p-0 text-start font-normal hover:bg-transparent"
          onclick={() => {
            selected = activity
            detailDialog.setOpen(true)
          }}>
          <div class="flex items-center gap-1 text-xs text-muted-foreground">
            <div class="tabular-nums tracking-tight">{activity.date.toDateFormat("dd/MM/yyyy")}</div>
            <div class="text-[0.5rem]">|</div>
            <div class="tabular-nums tracking-tight">
              {activity.at_start.toDateFormat("HH:mm")} s.d. {activity.at_end.toDateFormat("HH:mm")}
            </div>
            <div class="flex-1"></div>
            <div
              class="flex-wrap"
              class:text-green-500={Boolean(activity.approval_status.id)}>
              {activity.approval_at.toDateFormat("dd/MM/yyyy HH:mm")}
            </div>
          </div>
          <div>{activity.customer.name}</div>
          <div>{activity.name}</div>
        </Button>
        <Checkbox
          class="size-5 shadow-none"
          checked={selects[activity.id] ?? false}
          onCheckedChange={(value) => (selects = { ...selects, [activity.id]: value })}/>
      </div>
    {/each}
    <div
      bind:this={bottomOfPage}
      class="h-1">
    </div>
  </div>
  <div class="fixed inset-x-0 bottom-0 z-40 mx-auto flex w-full max-w-3xl justify-end pb-4 pe-4">
    <Button
      variant="default"
      size="icon-lg"
      class="rounded-full shadow"
      onclick={handleApprove}>
      <SaveIcon aria-hidden="true"/>
    </Button>
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected}/>
<DialogFormApproval
  open={approvalDialog.state}
  setOpen={approvalDialog.setOpen}
  selects={selects}/>
